import { z } from "zod";

import { VibeEnvVarNameSchema } from "./env-vars.schemas";
import { VibeVaultPathSchema } from "./secret-refs.schemas";

/**
 * A Vibe app's entitlement to one Nexus credential.
 *
 * The row that makes NEX-2718 possible: the app receives the credential's
 * material at runtime by REFERENCE, so nobody re-pastes a key into an env var
 * and central rotation and revocation keep working.
 *
 * 🔴 `materialFingerprint` IS DELIBERATELY ABSENT FROM THIS DTO. It is a salted
 * digest of secret material and no client has a use for it — `isMaterialized`
 * and `lastMaterializedAt` answer every question the UI actually asks ("has this
 * reached the app, and when"). A digest that never leaves the server cannot be
 * correlated against anything, which is the whole reason the salting exists.
 */
export const VibeAppCredentialBindingSchema = z.object({
  id: z.string().uuid(),
  vibeAppId: z.string().uuid(),
  organizationId: z.string(),
  credentialId: z.string().uuid(),
  /** The env var the material lands on. Owned by the secret ref, echoed here. */
  envVarName: VibeEnvVarNameSchema,
  vaultPath: VibeVaultPathSchema,
  /** Whether the platform has sealed material for this binding at least once. */
  isMaterialized: z.boolean(),
  lastMaterializedAt: z.coerce.date().nullable(),
  createdByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeAppCredentialBindingDto = z.infer<typeof VibeAppCredentialBindingSchema>;

// ----------------------------------------------------------------------------
// List credential bindings
// ----------------------------------------------------------------------------

export const ListVibeAppCredentialBindingsPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const ListVibeAppCredentialBindingsResponseSchema = z.object({
  bindings: z.array(VibeAppCredentialBindingSchema)
});

// ----------------------------------------------------------------------------
// Bind a credential
// ----------------------------------------------------------------------------

export const BindVibeAppCredentialPathVarsSchema = z.object({
  appId: z.string().uuid()
});

/**
 * The body carries a credential REFERENCE and a name — never a value. That is
 * the entire point of the feature, and it is why this schema has no `value`
 * field to omit by accident.
 */
export const BindVibeAppCredentialBodySchema = z.object({
  credentialId: z.string().uuid(),
  /**
   * Same SCREAMING_SNAKE_CASE shape as every other Vibe env var. The pointer the
   * platform mints under this name is PLATFORM-owned, so the tenant cannot
   * later overwrite or delete it out from under the binding.
   */
  envVarName: VibeEnvVarNameSchema
});

export const BindVibeAppCredentialResponseSchema = z.object({
  binding: VibeAppCredentialBindingSchema,
  /** False when the app was already bound to this credential and nothing changed. */
  created: z.boolean()
});

// ----------------------------------------------------------------------------
// Materialize a binding
// ----------------------------------------------------------------------------

export const MaterializeVibeAppCredentialPathVarsSchema = z.object({
  appId: z.string().uuid(),
  bindingId: z.string().uuid()
});

/**
 * 202-style ack. Returns the pending-write summary — NEVER the material, and
 * never its digest. `pendingWriteId` is null when the app already held exactly
 * this material and nothing was staged, which is the common outcome of calling
 * this on every deploy.
 */
export const MaterializeVibeAppCredentialResponseSchema = z.object({
  binding: VibeAppCredentialBindingSchema,
  pendingWriteId: z.string().uuid().nullable(),
  staged: z.boolean()
});

// ----------------------------------------------------------------------------
// Unbind
// ----------------------------------------------------------------------------

export const UnbindVibeAppCredentialPathVarsSchema = z.object({
  appId: z.string().uuid(),
  bindingId: z.string().uuid()
});

export const UnbindVibeAppCredentialResponseSchema = z.object({
  unboundId: z.string().uuid()
});
